import { css, cx } from "@emotion/css";
import { ExternalLink, IconButton, tableStyles, Tooltip } from "@octopusdeploy/design-system-components";
import { ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDApplicationResource, ArgoCDInstanceSummaryResource, EnvironmentV2Resource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { ArgoCDInstanceLinks } from "~/areas/infrastructure/components/ArgoCDInstances/argoCDInstanceLinks";
import { ArgoCDApplicationDetails, isMissingRequiredAnnotations, hasSourcesWithoutMatchedGitCredentials } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationDetails";
import { ArgoCDApplicationEnvironmentsChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationEnvironmentsChipList";
import { ArgoCDApplicationTenantsChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ArgoCDApplicationTenantsChipList";
import { QuickSkeleton } from "~/areas/tasks/components/Task/Kubernetes/components/QuickSkeleton";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { DataTable, DataTableBody, DataTableHeader, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
const styles = {
    headerRow: css({
        font: text.heading.xSmall,
        color: themeTokens.color.text.tertiary,
        margin: 0,
        textTransform: "uppercase",
        borderTop: "none !important",
    }),
    argoCDNameField: css({
        font: text.body.bold.medium,
        cursor: "pointer",
    }),
    navItemIcon: css({
        height: "20px",
        width: "100%",
    }),
    multiItemCellFlexContainer: css({
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "left",
        alignItems: "center",
        gap: space[32],
    }),
    indentedRow: css({
        paddingLeft: space[56],
    }),
    chipCell: css({
        alignItems: "center",
    }),
    applicationNameFlex: css({
        display: "inline-flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[16],
    }),
    applicationName: css({
        font: text.body.regular.medium,
    }),
    argoCDInstanceRow: css({
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    normalFont: css({
        fontWeight: "unset !important",
    }),
    fixedWidth(pixelWidth: string) {
        return css({
            width: pixelWidth,
        });
    },
    autoColumn: css({
        width: "auto",
    }),
    tenantsCell: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    }),
    footer: css({
        font: text.body.regular.xSmall,
        color: themeTokens.color.text.secondary,
        margin: space[16],
        marginBottom: 0,
    }),
};
const columns = [
    { name: "Argo CD Applications", className: styles.fixedWidth("550px") },
    { name: "Environments", className: styles.autoColumn },
    { name: "Tenants", className: styles.fixedWidth("305px") },
];
interface PreviewDataContext {
    allProjects: ProjectResource[];
    allEnvironments: EnvironmentV2Resource[];
    allTenants: TenantResource[];
}
interface LoadedData {
    argoCDInstances: ArgoCDInstanceSummaryResource[];
    applications: ArgoCDApplicationResource[];
    context: PreviewDataContext;
}
type DrawerProps = {
    onShowGenerateAnnotationsDrawer: (selectedApplicationId: string, selectedApplicationSourceIndex: number | undefined) => void;
};
type ArgoCDDeploymentPreviewProps = DrawerProps & {
    spaceId: string;
    query: InlineStatusQuery<LoadedData, null>;
    showScopingHelpText: boolean;
};
export function ArgoCDDeploymentPreview(props: ArgoCDDeploymentPreviewProps) {
    const { spaceId, query, showScopingHelpText, ...rest } = props;
    const link = links.argoCDInstancesPage.generateUrl({ spaceId });
    return (<>
            <DataTable title="Argo CD Applications" aria-label="argo cd applications" className={tableStyles}>
                <DataTableHeader>
                    <DataTableRow>
                        {columns.map((column) => (<HeaderCell key={column.name} text={column.name} columnClassName={column.className}/>))}
                    </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                    {query.isLoading && <PreviewTableSkeleton />}
                    {query.error && (<DataTableRow>
                            <DataTableRowColumn colSpan={columns.length}>
                                <div>Error: {query.error.message}</div>
                            </DataTableRowColumn>
                        </DataTableRow>)}
                    {!query.isLoading && !query.error && query.result && (<ProjectSpecificArgoPreview argoInstances={query.result.argoCDInstances} applications={query.result.applications} context={query.result.context} refetch={query.refetch} {...rest}/>)}
                </DataTableBody>
            </DataTable>
            {showScopingHelpText && !query.isLoading && !query.error && (<p className={styles.footer}>
                    This list only shows Argo CD instances with applications scoped to this project. <InternalLink to={link}>View all instances</InternalLink>
                </p>)}
        </>);
}
type BasePreviewProps = DrawerProps & {
    context: PreviewDataContext;
    refetch: () => void;
};
type ProjectSpecificArgoPreviewProps = BasePreviewProps & {
    argoInstances: ArgoCDInstanceSummaryResource[];
    applications: ArgoCDApplicationResource[];
};
function ProjectSpecificArgoPreview(props: ProjectSpecificArgoPreviewProps) {
    const { argoInstances, applications, ...rest } = props;
    //not-distinct, but won't matter.
    const referencedArgoCdInstancesIDs = applications
        .map((app) => app.ArgoCDGatewayId)
        .values()
        .toArray();
    const referencedArgoCDInstances = argoInstances.filter((instance) => referencedArgoCdInstancesIDs.includes(instance.Id));
    return (<>
            {referencedArgoCDInstances.map((argoInstance, index) => (<ArgoCDGatewayExpandableRow key={argoInstance.Id} argoInstance={argoInstance} applications={applications.filter((app) => app.ArgoCDGatewayId === argoInstance.Id)} startExpanded={index === 0} {...rest}/>))}
        </>);
}
type ArgoCDGatewayExpandableRowProps = BasePreviewProps & {
    argoInstance: ArgoCDInstanceSummaryResource;
    applications: ArgoCDApplicationResource[];
    startExpanded: boolean;
};
export function ArgoCDGatewayExpandableRow(props: ArgoCDGatewayExpandableRowProps) {
    const { argoInstance, applications, startExpanded, ...rest } = props;
    const [isExpanded, setIsExpanded] = React.useState(startExpanded);
    return (<>
            <DataTableRow key={argoInstance.Id} className={styles.argoCDInstanceRow}>
                <DataTableRowColumn>
                    <div className={cx(styles.multiItemCellFlexContainer)}>
                        <span>
                            <IconButton onClick={() => setIsExpanded(!isExpanded)} icon={isExpanded ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={`Toggle ${argoInstance.Id}`}/>
                        </span>
                        <span className={styles.argoCDNameField}>{argoInstance.Name}</span>
                    </div>
                </DataTableRowColumn>
                <DataTableRowColumn />
                <DataTableRowColumn />
            </DataTableRow>
            {isExpanded && (<>
                    {applications.map((app) => (<ArgoCDApplicationExpanderRow key={`application-${app.Name}`} argoInstance={argoInstance} application={app} {...rest}/>))}
                </>)}
        </>);
}
type ArgoCDApplicationRowProps = BasePreviewProps & {
    argoInstance: ArgoCDInstanceSummaryResource;
    application: ArgoCDApplicationResource;
};
export function ArgoCDApplicationExpanderRow(props: ArgoCDApplicationRowProps) {
    const { argoInstance, application, context, refetch, onShowGenerateAnnotationsDrawer } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const annotationIssue = isMissingRequiredAnnotations(application);
    const hasCredentialIssue = hasSourcesWithoutMatchedGitCredentials(application);
    const warningMessage = hasCredentialIssue ? "Requires Git credential" : annotationIssue ? "Missing required annotations" : undefined;
    const links = argoInstance.WebUIUri ? new ArgoCDInstanceLinks(argoInstance.WebUIUri) : undefined;
    return (<>
            <DataTableRow>
                <DataTableRowColumn>
                    <div className={cx(styles.multiItemCellFlexContainer)}>
                        <span className={styles.indentedRow}>
                            <IconButton onClick={() => setIsExpanded(!isExpanded)} icon={isExpanded ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={`Toggle ${application.Name}`}/>
                        </span>
                        <span className={styles.applicationNameFlex}>
                            <span className={styles.applicationName}>{application.Name}</span>
                            {links && <ExternalLink href={links.applicationDetails(application.Name, application.KubernetesNamespace)}>{""}</ExternalLink>}
                        </span>
                    </div>
                </DataTableRowColumn>
                <DataTableRowColumn className={styles.chipCell}>
                    <ArgoCDApplicationEnvironmentsChipList application={application} environments={context.allEnvironments}/>
                </DataTableRowColumn>
                <DataTableRowColumn className={styles.chipCell}>
                    <div className={styles.tenantsCell}>
                        <ArgoCDApplicationTenantsChipList application={application} tenants={context.allTenants}/>
                        {warningMessage && (<Tooltip content={warningMessage}>
                                <ExclamationTriangleIcon size={20} color={themeTokens.color.icon.warning}/>
                            </Tooltip>)}
                    </div>
                </DataTableRowColumn>
            </DataTableRow>
            {isExpanded && (<DataTableRow>
                    {/*The DataTable enforces bold text on FIRST element of column - which is not legal in this instance.*/}
                    <DataTableRowColumn fullWidth={true} fullHeight={true} colSpan={columns.length} className={styles.normalFont}>
                        <ArgoCDApplicationDetails application={application} onGitCredentialAdded={() => refetch()} onGenerateAnnotationsClick={(applicationSourceId) => onShowGenerateAnnotationsDrawer(application.Id, applicationSourceId)} projects={context.allProjects} environments={context.allEnvironments} tenants={context.allTenants}/>
                    </DataTableRowColumn>
                </DataTableRow>)}
        </>);
}
export const PreviewTableSkeleton = () => {
    const countSkeletonRow = 5;
    return (<>
            {Array.from({ length: countSkeletonRow }).map((_, j) => (<DataTableRow key={j}>
                    {Array.from({ length: columns.length }).map((_, i) => (<DataTableRowColumn key={i}>
                            <div className={styles.navItemIcon}>
                                <QuickSkeleton />
                            </div>
                        </DataTableRowColumn>))}
                </DataTableRow>))}
        </>);
};
function HeaderCell(props: {
    text: string;
    columnClassName: string;
}) {
    return (<DataTableRowColumn className={cx(styles.headerRow, props.columnClassName)}>
            <h2 className={styles.headerRow}>{props.text}</h2>
        </DataTableRowColumn>);
}
